import * as AlertDialogPrimitive from '@radix-ui/react-alert-dialog';
import * as React from 'react';

import { buttonVariants } from '@components/ui/button';
import { cn } from '@lib/utils';

const AlertDialog = AlertDialogPrimitive.Root;

const AlertDialogTrigger = AlertDialogPrimitive.Trigger;

const AlertDialogPortal = AlertDialogPrimitive.Portal;

const AlertDialogOverlay = (props: React.ComponentProps<typeof AlertDialogPrimitive.Overlay>) => {
  const { className, ...otherProps } = props;

  return (
    <AlertDialogPrimitive.Overlay
      className={cn(
        'fixed inset-0 z-50 bg-black/80 backdrop-blur-[1px] data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
        className,
      )}
      {...otherProps}
    />
  );
};
AlertDialogOverlay.displayName = AlertDialogPrimitive.Overlay.displayName;

const AlertDialogContent = (props: React.ComponentProps<typeof AlertDialogPrimitive.Content>) => {
  const { className, ...otherProps } = props;

  return (
    <AlertDialogPortal>
      <AlertDialogOverlay />
      <AlertDialogPrimitive.Content
        className={cn(
          'fixed left-[50%] top-[50%] z-50 grid w-full max-w-lg translate-x-[-50%] translate-y-[-50%] gap-4 border bg-background p-6 shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] sm:rounded-lg',
          className,
        )}
        {...otherProps}
      />
    </AlertDialogPortal>
  );
};
AlertDialogContent.displayName = AlertDialogPrimitive.Content.displayName;

const AlertDialogHeader = (props: React.ComponentProps<'div'>) => {
  const { className, ...otherProps } = props;

  return (
    <div
      className={cn('flex flex-col space-y-2 text-center sm:text-left', className)}
      {...otherProps}
    />
  );
};
AlertDialogHeader.displayName = 'AlertDialogHeader';

const AlertDialogFooter = (props: React.ComponentProps<'div'>) => {
  const { className, ...otherProps } = props;

  return (
    <div
      className={cn('flex flex-col-reverse sm:flex-row sm:justify-end sm:gap-x-2', className)}
      {...otherProps}
    />
  );
};
AlertDialogFooter.displayName = 'AlertDialogFooter';

const AlertDialogTitle = (props: React.ComponentProps<typeof AlertDialogPrimitive.Title>) => {
  const { className, ...otherProps } = props;

  return (
    <AlertDialogPrimitive.Title
      className={cn('text-xl font-medium', className)}
      {...otherProps}
    />
  );
};
AlertDialogTitle.displayName = AlertDialogPrimitive.Title.displayName;

const AlertDialogDescription = (props: React.ComponentProps<typeof AlertDialogPrimitive.Description>) => {
  const { className, ...otherProps } = props;

  return (
    <AlertDialogPrimitive.Description
      className={cn('text-sm text-muted-foreground', className)}
      {...otherProps}
    />
  );
};
AlertDialogDescription.displayName = AlertDialogPrimitive.Description.displayName;

type AlertDialogActionProps = React.ComponentProps<typeof AlertDialogPrimitive.Action> & {
  variant?: 'default' | 'danger';
};

const AlertDialogAction = (props: AlertDialogActionProps) => {
  const { className, variant = 'default', ...otherProps } = props;

  return (
    <AlertDialogPrimitive.Action
      className={cn(
        'inline-flex h-10 items-center justify-center rounded-md px-4 py-2 text-sm font-medium transition-colors',
        'focus:outline-none focus:ring-2 focus:ring-offset-2',
        'disabled:opacity-50 disabled:pointer-events-none',
        variant === 'danger' && 'bg-red-100 text-red-600 hover:bg-red-200',
        className,
      )}
      {...otherProps}
    />
  );
};
AlertDialogAction.displayName = AlertDialogPrimitive.Action.displayName;

const AlertDialogCancel = (props: React.ComponentProps<typeof AlertDialogPrimitive.Cancel>) => {
  const { className, ...otherProps } = props;

  return (
    <AlertDialogPrimitive.Cancel
      className={cn(buttonVariants({ variant: 'outline' }), 'mt-2 sm:mt-0', className)}
      {...otherProps}
    />
  );
};
AlertDialogCancel.displayName = AlertDialogPrimitive.Cancel.displayName;

export {
  AlertDialog,
  AlertDialogPortal,
  AlertDialogOverlay,
  AlertDialogTrigger,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogAction,
  AlertDialogCancel,
};
